import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { CENTRUM_URL } from '../../../utils/env';
import { FilterNotifications } from '../../../schemas/indexableFiltersSchema';
import type { z } from 'zod';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useEffect } from 'react';
import type { NotificationDismissedStorage } from '../util';
import { LS_KEY } from '../util';
import { FILTER_NOTIFICATIONS_QUERY_KEY } from '../../../queries/keys/ifm/ifm';
import { authenticatedHeaders } from '../../../queries/headers';

export type FilterNotificationsType = z.infer<typeof FilterNotifications>;
export const useGetFilterNotifications = (enabled = true) => {
  const auth0 = useAuth0();
  const [ls, setValue] = useLocalStorage<NotificationDismissedStorage>(LS_KEY, []);

  const { data, isLoading } = useQuery({
    queryKey: [FILTER_NOTIFICATIONS_QUERY_KEY],
    refetchOnMount: false,
    enabled,
    retry: false,
    queryFn: async () => {
      const token = await auth0.getAccessTokenSilently();
      try {
        const request = await fetch(`${CENTRUM_URL}/rrm-rc/ifm-rc/notification`, {
          headers: authenticatedHeaders(token),
        });

        const json = await request.json();
        if (request.ok) {
          return FilterNotifications.parse(json);
        }
        return Promise.reject(new Error(json.apierror.message));
      } catch (error) {
        return Promise.reject(new Error('Failed to get filter notifications'));
      }
    },
  });

  useEffect(() => {
    if (isLoading || !ls.length) return;

    if (!data?.length) {
      setValue([]);
      return;
    }
    const filterNotifications = (dismissedNotification: {
      categoryId: string;
      notificationType: string;
    }) => {
      return data.some((notification) => {
        const foundNotificationType =
          notification.affectedCategories.some(
            (s) => s.externalCategoryId === dismissedNotification.categoryId,
          ) &&
          notification.notificationType === dismissedNotification.notificationType;

        return foundNotificationType;
      });
    };

    const newLs = ls.filter(filterNotifications);

    if (newLs.length === 0) {
      setValue([]);
    } else if (newLs.length !== ls.length) {
      setValue(newLs);
    }
  }, [data, ls, setValue, isLoading]);

  return { data, isLoading };
};
